import { Bundle, Resource } from "@medplum/fhirtypes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { EhrDiff } from "../../../domain/ehr";
import { capture } from "../../../shared/capture";
import { swallowBadRequestError, swallowTimeoutOrNetworkError } from "../../error/utils";
import { MappedConsolidatedResources } from "./consolidated-context/reducer";
import { useBundleGroupingWorker } from "./useBundleGroupingWorker";
import useMetriportToast from "./useMetriportToast";

type UseGetEhrDiffRQ = {
  isFetchingDiff: boolean;
  metriportOnlyBundle: Bundle<Resource> | undefined;
  metriportOnlyBundleResources: MappedConsolidatedResources | undefined;
  metriportOnlyResourceTypes: string[] | undefined;
  getMetriportOnlyBundleJob: () => Promise<void>;
  onInvalidateEhrDiff: () => void;
};

const POLLING_INTERVAL = dayjs.duration(1, "second").asMilliseconds();
const EXPLAIN_TOAST_DURATION = dayjs.duration(25, "second").asMilliseconds();

const EHR_DIFF_QUERY_KEY = "ehr-diff";
const EHR_DIFF_PROCESSED_QUERY_KEY = "ehr-diff-processed";
const EHR_DIFF_ERROR_MESSAGE = "Failed to get patient resource diff";
const EHR_DIFF_SUCCESS_MESSAGE =
  "The patient's resource difference between Metriport and EHR data is ready.";
const START_COMPUTE_MESSAGE =
  "Computing the patient's resource difference between Metriport and EHR data. This may take some time depending on how much data is available.";
const START_COMPUTE_ERROR_MESSAGE = "Failed to start patient resource difference query";
const GET_METRIPORT_ONLY_BUNDLE_ERROR_MESSAGE = "Failed to get latest patient resource diff";

const ehrDiffKeys = {
  diff: (ehrPatientId: string, jobId: string) => [EHR_DIFF_QUERY_KEY, ehrPatientId, jobId] as const,
  processed: (ehrPatientId: string, jobId: string) =>
    [EHR_DIFF_PROCESSED_QUERY_KEY, ehrPatientId, jobId] as const,
};

interface UseGetEhrDiffRQParams {
  ehr?: string;
  ehrPatientId?: string;
  diff?: EhrDiff;
}

export function useGetEhrDiffRQ({
  ehr,
  ehrPatientId,
  diff,
}: UseGetEhrDiffRQParams): UseGetEhrDiffRQ {
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [isPollLoading, setIsPollLoading] = useState(false);

  const toast = useMetriportToast();
  const processBundle = useBundleGroupingWorker();
  const invalidateEhrDiff = useInvalidateEhrDiff();

  const isPollEnabled = !!ehrPatientId && !!jobId && !!diff && !!ehr;

  // 1. Poll for diff job status and download bundle when ready
  const pollDiffQuery = useQuery({
    queryKey: ehrDiffKeys.diff(ehrPatientId || "", jobId || ""),
    queryFn: async () => {
      setIsPollLoading(true);
      return await getDiffBundle(ehrPatientId, jobId, diff);
    },
    enabled: isPollEnabled,
    refetchIntervalInBackground: true,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: false,
    refetchInterval: query => {
      if (!isPollEnabled) return false;
      const data = query.state.data;
      if (data?.status === "completed" || data?.status === "failed") {
        setIsPollLoading(false);
        return false;
      }
      return POLLING_INTERVAL;
    },
    meta: {
      errorMessage: EHR_DIFF_ERROR_MESSAGE,
      toastDuration: EXPLAIN_TOAST_DURATION,
      toast: toast,
    },
  });

  const metriportOnlyBundle = pollDiffQuery.data?.bundle;
  const metriportOnlyResourceTypes = pollDiffQuery.data?.resourceTypes;
  const pollError = pollDiffQuery.error;

  useEffect(() => {
    if (!pollError) return;
    setJobId(undefined);
    setIsPollLoading(false);
    capture.error(EHR_DIFF_ERROR_MESSAGE, {
      extra: {
        patient: ehrPatientId,
        context: `patient.ehr.get-metriport-only-bundle`,
        error: pollError,
      },
    });
  }, [pollError]);

  // 2. Process bundle with web worker
  const processedDiffQuery = useQuery({
    queryKey: ehrDiffKeys.processed(ehrPatientId || "", jobId || ""),
    queryFn: () => (metriportOnlyBundle ? processBundle(metriportOnlyBundle) : undefined),
    enabled: !!metriportOnlyBundle && !!ehrPatientId,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    meta: {
      errorMessage: EHR_DIFF_ERROR_MESSAGE,
      successMessage: EHR_DIFF_SUCCESS_MESSAGE,
      toastDuration: EXPLAIN_TOAST_DURATION,
      toast: toast,
    },
  });

  const metriportOnlyBundleResources = processedDiffQuery.data;

  const startDiffJob = useMutation({
    mutationFn: async () => {
      if (!diff || !ehr || !ehrPatientId) return;
      return await diff.startResourceDiffJob(ehrPatientId);
    },
    onSuccess: jobId => {
      if (!jobId) return;
      setJobId(jobId);
      toast.info({ title: START_COMPUTE_MESSAGE, duration: EXPLAIN_TOAST_DURATION });
    },
    onError: error => {
      const { handled } = swallowBadRequestError(error);
      if (!handled) {
        capture.error(START_COMPUTE_ERROR_MESSAGE, {
          extra: {
            patient: ehrPatientId,
            context: `patient.ehr.start-metriport-only-job`,
            error,
          },
        });
      }
      toast.error({ title: START_COMPUTE_ERROR_MESSAGE });
      setJobId(undefined);
    },
  });

  async function getMetriportOnlyBundleJob() {
    try {
      if (!diff || !ehr || !ehrPatientId) return;
      const jobRequest = await diff.getResourceDiffJob(ehrPatientId);

      if (!jobRequest || jobRequest.metadata.status === "failed") {
        await startDiffJob.mutateAsync();
      } else if (jobRequest.response) {
        setJobId(jobRequest.metadata.id);
      } else {
        setJobId(jobRequest.metadata.id);
      }
    } catch (error) {
      capture.error(GET_METRIPORT_ONLY_BUNDLE_ERROR_MESSAGE, {
        extra: { patient: ehrPatientId, context: `patient.ehr.get-metriport-only-bundle`, error },
      });
      toast.error({ title: GET_METRIPORT_ONLY_BUNDLE_ERROR_MESSAGE });
    }
  }

  function onInvalidateEhrDiff() {
    invalidateEhrDiff(ehrPatientId || "", jobId || "");
  }

  const isFetchingDiff = startDiffJob.isPending || isPollLoading;

  return {
    isFetchingDiff,
    metriportOnlyBundle,
    metriportOnlyResourceTypes,
    metriportOnlyBundleResources,
    getMetriportOnlyBundleJob,
    onInvalidateEhrDiff,
  };
}

export function useInvalidateEhrDiff() {
  const queryClient = useQueryClient();
  return (ehrPatientId: string, jobId: string) => {
    queryClient.invalidateQueries({ queryKey: ehrDiffKeys.diff(ehrPatientId, jobId) });
    queryClient.invalidateQueries({ queryKey: ehrDiffKeys.processed(ehrPatientId, jobId) });
  };
}

interface GetDiffBundleResult {
  status: string;
  bundle?: Bundle<Resource>;
  resourceTypes?: string[];
}

async function getDiffBundle(
  ehrPatientId?: string,
  jobId?: string,
  diff?: EhrDiff
): Promise<GetDiffBundleResult | undefined> {
  try {
    if (!ehrPatientId || !jobId || !diff) return;

    const jobRequest = await diff.getResourceDiffJob(ehrPatientId, jobId);

    if (!jobRequest) throw new Error("Job not found");

    if (jobRequest.metadata.status === "failed") throw new Error("Job failed");

    if (jobRequest.response) {
      const bundle = await downloadBundlesAndMerge(jobRequest.response.preSignedUrls);
      const resourceTypes = jobRequest.response.resourceTypes;
      return { status: "completed", bundle, resourceTypes };
    }

    return { status: "processing" };
  } catch (error) {
    const { handled } = swallowTimeoutOrNetworkError(error);
    if (handled) return { status: "processing" };
    throw error;
  }
}

// Reuse the existing bundle download function
async function downloadBundlesAndMerge(preSignedUrls: string[]): Promise<Bundle<Resource>> {
  const bundles: PromiseSettledResult<Bundle<Resource>>[] = await Promise.allSettled(
    preSignedUrls.map(async fileUrl => {
      const response = await axios.get<Bundle<Resource>>(fileUrl);
      if (!response.data) throw new Error("No data returned from bundle");
      return response.data;
    })
  );

  const emptyBundle = {
    resourceType: "Bundle",
    entry: [],
  } as Bundle<Resource>;

  const bundle: Bundle<Resource> = bundles.reduce((acc, curr) => {
    if (curr.status === "fulfilled") {
      acc.entry?.push(...(curr.value.entry ?? []));
    }
    return acc;
  }, emptyBundle);

  return bundle;
}
